'use client'

import { IconChevronRight } from '@macarongfactory/web-icons'
import { Chip, FlexibleChipGroup, Text } from '@macarongfactory/web-ui'
import Link from 'next/link'

import RankingListItem from './RankingListItem'
import {
  HOME_COPY,
  HOME_TRACK_CODE,
  HOME_TRACK_LABEL,
  HOME_TRACK_TYPE,
  RANKING_CRITERIA,
  RANKING_PATH,
  useCarRanking,
  useHomeImpression,
  useHomeTracking,
} from '../_features/modoocar-home'

import type { TRankingCriteria, TRankingMap } from '../_features/modoocar-home'

const MOBILE_MORE_CHEVRON_PX = 16
const DESKTOP_MORE_CHEVRON_PX = 20

const CarRankingSection = ({ rankingMap }: { rankingMap: TRankingMap }) => {
  const { criteria, items, selectCriteria } = useCarRanking(rankingMap)
  const { sectionShow, itemClick, setRankType } = useHomeTracking()

  const sectionRef = useHomeImpression<HTMLElement>(() =>
    sectionShow(HOME_TRACK_CODE.HOME_RANK, HOME_TRACK_LABEL.RANK_SECTION)
  )

  const handleSelectCriteria = (key: TRankingCriteria, label: string) => {
    selectCriteria(key)
    setRankType(key)
    itemClick(HOME_TRACK_CODE.HOME_RANK, HOME_TRACK_TYPE.CHIP, label)
  }

  return (
    <section
      ref={sectionRef}
      className='flex flex-col bg-background-surface-default pb-dimension-x6'
    >
      <div className='flex items-start justify-center gap-dimension-x3 pl-dimension-x5 pr-dimension-x2 pt-dimension-x8'>
        <Text
          as='h2'
          variant='heading-2-semibold'
          color='text-foreground-neutral-strong'
          className='flex-1'
        >
          {HOME_COPY.rankingTitle}
        </Text>
        <Link
          href={RANKING_PATH}
          className='flex shrink-0 items-center gap-dimension-x0-5 px-dimension-x1-5 py-dimension-x1'
          onClick={() =>
            itemClick(
              HOME_TRACK_CODE.HOME_RANK,
              HOME_TRACK_TYPE.BUTTON,
              HOME_TRACK_LABEL.RANK_MORE
            )
          }
        >
          <Text
            variant='title-2-semibold'
            color='text-foreground-neutral-alternative'
          >
            {HOME_COPY.rankingMore}
          </Text>
          <span className='inline-flex text-foreground-neutral-alternative max-[639px]:hidden'>
            <IconChevronRight
              width={DESKTOP_MORE_CHEVRON_PX}
              height={DESKTOP_MORE_CHEVRON_PX}
            />
          </span>
          <span className='hidden text-foreground-neutral-alternative max-[639px]:inline-flex'>
            <IconChevronRight
              width={MOBILE_MORE_CHEVRON_PX}
              height={MOBILE_MORE_CHEVRON_PX}
            />
          </span>
        </Link>
      </div>

      <FlexibleChipGroup>
        {RANKING_CRITERIA.map(({ key, label }) => (
          <Chip.Filter
            key={key}
            text={label}
            selected={criteria === key}
            clickable={() => handleSelectCriteria(key, label)}
          />
        ))}
      </FlexibleChipGroup>

      <ul className='flex flex-col gap-dimension-x2 px-dimension-x5 pb-dimension-x8 max-[639px]:gap-0 max-[639px]:px-0 max-[639px]:pb-0'>
        {items.map((item, index) => (
          <li key={`${item.brand}-${item.model}`}>
            <RankingListItem
              item={item}
              criteria={criteria}
              isFirst={index === 0}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

export default CarRankingSection
